import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Form, Input, Radio, Space, Typography } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { MailOutlined, WhatsAppOutlined } from "@ant-design/icons";
import axios from "axios";
import API_CONFIG from "../../config/api.js";
import { ELSA_BRAND_ASSETS } from "../../config/brandAssets";
import useRecoveryCooldown from "../../hooks/useRecoveryCooldown";
import "./PasswordReset.css";

const { Title, Text } = Typography;

export default function ForgotPassword() {
  const [form] = Form.useForm();
  const [codeForm] = Form.useForm();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [busy, setBusy] = useState("");
  const [serverMessage, setServerMessage] = useState("");
  const [serverError, setServerError] = useState("");
  const [cooldown, setCooldown] = useRecoveryCooldown();
  const generation = useRef(0);
  const pending = useRef(null);
  const channel = Form.useWatch("channel", form) || "whatsapp";

  useEffect(() => () => { generation.current += 1; pending.current?.abort(); }, []);
  const clearRequest = () => {
    generation.current += 1;
    pending.current?.abort();
    pending.current = null;
    setBusy("");
    setRequest(null);
    setServerMessage("");
    setServerError("");
    setCooldown(0);
  };
  const startRequest = async (selectedChannel) => {
    if (pending.current) return;
    let values;
    try { values = await form.validateFields(["email"]); } catch (_) { return; }
    if (pending.current) return;
    const operation = ++generation.current;
    const controller = new AbortController();
    pending.current = controller;
    setBusy("request");
    setServerError("");
    setServerMessage("");
    try {
      const { data } = await axios.post(API_CONFIG.apiUrl("/password-reset/forgot"),
        { email: values.email, channel: selectedChannel }, { signal: controller.signal });
      if (operation !== generation.current) return;
      if (!/^[a-f0-9]{48}$/.test(data.requestId || "")) throw new Error("INVALID_RESPONSE");
      form.setFieldsValue({ channel: selectedChannel });
      setRequest({ requestId: data.requestId, channel: selectedChannel });
      if (request?.channel === "whatsapp") codeForm.resetFields();
      setCooldown(Number(data.retryAfter) || 60);
      setServerMessage(data.message);
    } catch (error) {
      if (operation === generation.current && error.code !== "ERR_CANCELED") {
        setServerError(error.response?.data?.message || "La demande n’a pas pu être confirmée. Réessayez ou utilisez l’email.");
      }
    } finally {
      if (operation === generation.current) { pending.current = null; setBusy(""); }
    }
  };
  const verifyCode = async ({ code }) => {
    if (!request || pending.current) return;
    const operation = ++generation.current;
    const controller = new AbortController();
    pending.current = controller;
    setBusy("verify");
    setServerError("");
    try {
      const { data } = await axios.post(API_CONFIG.apiUrl("/password-reset/verify"),
        { requestId: request.requestId, code: String(code).trim() }, { signal: controller.signal });
      if (operation !== generation.current) return;
      if (!/^[a-f0-9]{64}$/.test(data.token || "")) throw new Error("INVALID_RESPONSE");
      codeForm.resetFields();
      // No code or reset grant in localStorage or in the WhatsApp conversation.
      navigate("/reset-password", { replace: true, state: { resetToken: data.token } });
    } catch (error) {
      if (operation === generation.current && error.code !== "ERR_CANCELED") {
        setServerError(error.response?.data?.message || "Le code n’a pas pu être vérifié. Réessayez.");
      }
    } finally {
      if (operation === generation.current) { pending.current = null; setBusy(""); }
    }
  };

  return <div className="password-reset-shell">
    <Card className="password-reset-card">
      <div className="password-reset-brand">
        <img src={ELSA_BRAND_ASSETS.logoHorizontalColor} alt="ELSA GESTION" className="password-reset-brand__logo password-reset-brand__logo--light" />
        <img src={ELSA_BRAND_ASSETS.logoHorizontalWhite} alt="ELSA GESTION" className="password-reset-brand__logo password-reset-brand__logo--dark" />
        <span className="password-reset-brand__eyebrow">Sécurité du compte</span>
        <Title level={3} className="password-reset-brand__title">Mot de passe oublié</Title>
        <Text className="password-reset-brand__subtitle">
          Recevez un code sur votre WhatsApp vérifié ou un lien par email.
        </Text>
      </div>
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        {serverMessage && <Alert type="info" showIcon message={serverMessage} />}
        {serverError && <Alert type="error" showIcon message={serverError} />}
        <Form form={form} layout="vertical" initialValues={{ channel: "whatsapp" }} requiredMark={false}
          className="password-reset-form" onValuesChange={clearRequest}
          onFinish={() => startRequest(channel)}>
          <Form.Item name="email" label="Email de connexion" rules={[
            { required: true, message: "Renseignez votre email de connexion." },
            { type: "email", message: "Renseignez un email valide." }, { max: 254 },
          ]}>
            <Input prefix={<MailOutlined />} autoComplete="email" type="email" size="large" placeholder="votre@email.com" />
          </Form.Item>
          <Form.Item name="channel" label="Recevoir les instructions par">
            <Radio.Group className="password-reset-channels">
              <Radio value="whatsapp"><WhatsAppOutlined /> WhatsApp</Radio>
              <Radio value="email"><MailOutlined /> Email</Radio>
            </Radio.Group>
          </Form.Item>
          <p className="password-reset-help">
            WhatsApp utilise le numéro personnel vérifié dans « Mon profil → Récupération du compte ».
            Sans numéro vérifié, choisissez l’email.
          </p>
          <Button type="primary" htmlType="submit" size="large" block className="password-reset-button"
            loading={busy === "request"} disabled={Boolean(busy) || cooldown > 0}>
            {cooldown > 0 ? "Nouvelle demande dans " + cooldown + " s"
              : request ? "Renvoyer les instructions"
                : channel === "whatsapp" ? "Recevoir un code WhatsApp" : "Recevoir un lien par email"}
          </Button>
        </Form>
        {request?.channel === "whatsapp" && <>
          <Form form={codeForm} clearOnDestroy layout="vertical" requiredMark={false} onFinish={verifyCode}
            className="password-reset-form">
            <Form.Item name="code" label="Code WhatsApp" rules={[
              { required: true, message: "Renseignez le code reçu." },
              { pattern: /^\d{6}$/, message: "Le code contient 6 chiffres." },
            ]}>
              <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} size="large" />
            </Form.Item>
            <p className="password-reset-help">Le code est valable 5 minutes et ne peut servir qu’une seule fois.</p>
            <Button type="primary" htmlType="submit" block loading={busy === "verify"} disabled={Boolean(busy)}>
              Vérifier le code
            </Button>
          </Form>
          <Button type="link" block disabled={Boolean(busy)}
            onClick={() => startRequest("email")}>Recevoir un lien par email à la place</Button>
        </>}
        {request?.channel === "email" && <Text type="secondary">
          Consultez votre boîte de réception et les courriers indésirables. Le lien est valable 30 minutes.
        </Text>}
        <Text type="secondary">Si vous n’avez plus accès à ces moyens de récupération, contactez votre administrateur.</Text>
      </Space>
      <div className="password-reset-footer">
        <p className="password-reset-footer__contact"><a href="https://www.elsagestion.com">www.elsagestion.com</a><br />
          <a href="tel:+22664989099">+226 64 98 90 99</a></p>
        <Link to="/login" className="password-reset-footer__back">Retour à la connexion</Link>
      </div>
    </Card>
  </div>;
}
